import { $api } from './useApi'

export interface ActivityLogEntry {
    id?: string
    action: 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'VIEW'
    entityType: string
    entityId?: string
    details?: string
    timestamp?: string
    userName?: string
    diff?: any
    snapshot?: any
}

export const useActivityLog = () => {
    const logAction = async (entry: ActivityLogEntry): Promise<void> => {
        // Activity logging is now handled server-side for better security and reliability.
        // This function is kept for backward compatibility with existing components but does nothing.
        return Promise.resolve();
    }

    const getLogs = async (params?: {
        userId?: string
        entityType?: string
        action?: string
        from?: Date
        to?: Date
        page?: number
        pageSize?: number
    }): Promise<ActivityLogEntry[]> => {
        try {
            const query: Record<string, string> = {}
            if (params?.userId) query.userId = params.userId
            if (params?.entityType) query.entityType = params.entityType
            if (params?.action) query.action = params.action
            if (params?.from) query.from = params.from.toISOString()
            if (params?.to) query.to = params.to.toISOString()
            if (params?.page) query.page = params.page.toString()
            if (params?.pageSize) query.pageSize = params.pageSize.toString()

            const data = await $api<any[]>('/activitylogs', { query })
            return (data || []).map((l: any) => ({
                id: l.id,
                action: l.action,
                entityType: l.entityType,
                entityId: l.entityId,
                details: l.details,
                timestamp: l.timestamp,
                userName: l.userName,
                diff: l.diff ? JSON.parse(l.diff) : null,
                snapshot: l.snapshot ? JSON.parse(l.snapshot) : null
            }))
        } catch (e) {
            console.error('Failed to get logs:', e)
            return []
        }
    }

    const getMyLogs = async (limit = 50): Promise<ActivityLogEntry[]> => {
        try {
            const data = await $api<any[]>('/activitylogs/my', { query: { limit: limit.toString() } })
            return (data || []).map((l: any) => ({
                id: l.id,
                action: l.action,
                entityType: l.entityType,
                entityId: l.entityId,
                details: l.details,
                timestamp: l.timestamp,
                userName: l.userName,
                diff: l.diff ? JSON.parse(l.diff) : null,
                snapshot: l.snapshot ? JSON.parse(l.snapshot) : null
            }))
        } catch (e) {
            console.error('Failed to get my logs:', e)
            return []
        }
    }

    const getEntityLogs = async (entityType: string, entityId: string): Promise<ActivityLogEntry[]> => {
        try {
            const data = await $api<any[]>(`/activitylogs/entity/${entityType}/${entityId}`)
            return (data || []).map((l: any) => ({
                id: l.id,
                action: l.action,
                entityType: l.entityType,
                entityId: l.entityId,
                details: l.details,
                timestamp: l.timestamp,
                userName: l.userName,
                diff: l.diff ? JSON.parse(l.diff) : null,
                snapshot: l.snapshot ? JSON.parse(l.snapshot) : null
            }))
        } catch (e) {
            console.error('Failed to get entity logs:', e)
            return []
        }
    }

    const getStats = async (): Promise<{
        totalLogs: number
        todayLogs: number
        byAction: { action: string; count: number }[]
        byEntityType: { entityType: string; count: number }[]
    }> => {
        try {
            const data = await $api<any>('/activitylogs/stats')
            return {
                totalLogs: data?.totalLogs || 0,
                todayLogs: data?.todayLogs || 0,
                byAction: data?.byAction || [],
                byEntityType: data?.byEntityType || []
            }
        } catch (e) {
            console.error('Failed to get stats:', e)
            return { totalLogs: 0, todayLogs: 0, byAction: [], byEntityType: [] }
        }
    }

    return {
        logAction,
        getLogs,
        getMyLogs,
        getEntityLogs,
        getStats
    }
}
